import _ from "lodash";
import React, { useState, useEffect } from "react";

import { ShowLastYearSelect } from "./ShowLastYearSelect";
import { TimeUnitSelect } from "./TimeUnitSelect";
import {
  ChartData,
  createDataGroups,
  createEmptyChartData,
  compileToDataSource,
} from "./utils";
import { OrgTimeSeriesMetricsData } from "../../../../models/ChartCollectionData";
import LineChartItem from "../LineChartItem";
import { calculateGraphSize } from "../utils";

import "./styles.scss";

export interface ILineChartProps {
  chartDetailsData: OrgTimeSeriesMetricsData[];
  prevYearChartDetailsData?: OrgTimeSeriesMetricsData[];
  timeUnit: "day" | "week" | "month";
  usePrevYear: boolean;
  setTimeUnit: (unit: "day" | "week" | "month") => void;
  setUsePrevYear: (shouldUse: boolean) => void;
  skipLabelTranslation?: boolean;
}

export const LineChart: React.FunctionComponent<ILineChartProps> = (props) => {
  const {
    chartDetailsData,
    prevYearChartDetailsData,
    timeUnit,
    usePrevYear,
    setTimeUnit,
    setUsePrevYear,
    skipLabelTranslation,
  } = props;

  const emptyChartData = createEmptyChartData();

  const [chartDataDays, setChartDataDays] = useState<ChartData>({
    ...emptyChartData,
  });
  const [chartDataWeeks, setChartDataWeeks] = useState<ChartData>({
    ...emptyChartData,
  });
  const [chartDataMonths, setChartDataMonths] = useState<ChartData>({
    ...emptyChartData,
  });
  const [prevYearChartDataDays, setPrevYearChartDataDays] = useState<ChartData>(
    { ...emptyChartData }
  );
  const [prevYearChartDataWeeks, setPrevYearChartDataWeeks] =
    useState<ChartData>({ ...emptyChartData });
  const [prevYearChartDataMonths, setPrevYearChartDataMonths] =
    useState<ChartData>({ ...emptyChartData });

  useEffect(() => {
    if (!!chartDetailsData) {
      const dataSourceConfig = compileToDataSource(
        chartDetailsData,
        skipLabelTranslation ?? false,
        false
      );

      const { chartDataDaysTemp, chartDataWeeksTemp, chartDataMonthsTemp } =
        createDataGroups(chartDetailsData, dataSourceConfig, false);

      setChartDataDays(_.cloneDeep(chartDataDaysTemp));
      setChartDataWeeks(_.cloneDeep(chartDataWeeksTemp));
      setChartDataMonths(_.cloneDeep(chartDataMonthsTemp));

      if (usePrevYear && !!prevYearChartDetailsData) {
        const prevYearConfig = compileToDataSource(
          prevYearChartDetailsData,
          skipLabelTranslation ?? false,
          true
        );

        const { chartDataDaysTemp, chartDataWeeksTemp, chartDataMonthsTemp } =
          createDataGroups(prevYearChartDetailsData, prevYearConfig, true);

        setPrevYearChartDataDays(_.cloneDeep(chartDataDaysTemp));
        setPrevYearChartDataWeeks(_.cloneDeep(chartDataWeeksTemp));
        setPrevYearChartDataMonths(_.cloneDeep(chartDataMonthsTemp));
      } else {
        setPrevYearChartDataDays({ ...emptyChartData });
        setPrevYearChartDataWeeks({ ...emptyChartData });
        setPrevYearChartDataMonths({ ...emptyChartData });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDetailsData, prevYearChartDetailsData, usePrevYear]);

  const getDataByTimeUnit = (prevYear = false) => {
    if (prevYear) {
      switch (timeUnit) {
        case "day":
          return prevYearChartDataDays;
        case "week":
          return prevYearChartDataWeeks;
        case "month":
          return prevYearChartDataMonths;
      }
    } else {
      switch (timeUnit) {
        case "day":
          return chartDataDays;
        case "week":
          return chartDataWeeks;
        case "month":
          return chartDataMonths;
      }
    }
  };

  return (
    <div className="collection-chart-area-line-chart">
      <TimeUnitSelect timeUnit={timeUnit} setTimeUnit={setTimeUnit} />
      <ShowLastYearSelect
        usePrevYear={usePrevYear}
        setUsePrevYear={setUsePrevYear}
      />
      <div className="line-chart">
        <LineChartItem
          {...calculateGraphSize(
            "line",
            chartDetailsData.length + (prevYearChartDetailsData?.length ?? 0)
          )}
          dataList={getDataByTimeUnit()}
          previousYearDataList={getDataByTimeUnit(true)}
          usePrevYear={usePrevYear}
        />
      </div>
    </div>
  );
};

export default LineChart;
